import { ApplicationConfig, importProvidersFrom, inject, Injectable, isDevMode, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { FormatConfigService } from './core/services/format-config.service';
import { environment } from '../environments/environment';
import { AuthConfig, OAuthModuleConfig, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { Location, registerLocaleData } from '@angular/common';
import { keysToTranslate } from '../i18n/keys-to-translate';
import { AppRoutingModule } from './app-routing.module';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { MizenAngularLibConfig, MizenAngularLibModule } from 'mizen-angular-lib';
import { TranslateModule } from '@ngx-translate/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de-CH';
import localeFr from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it-CH';

registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeIt);

const windowEnvironment = (window as any).environment;

@Injectable()
export class AppConfig {
  constructor(
    private formatConfigService: FormatConfigService,
    private location: Location,
    private oauth: OAuthService
  ) {
    keysToTranslate(['CodeGläubiger', 'CodeSchuldner', 'RolleGläubiger', 'Komponenten', 'MaximalResultWarning']);
  }

  public load(): Promise<any> {
    let redirectUri: string;
    if (environment.postLogoutRedirectUri === null || environment.postLogoutRedirectUri === undefined) {
      redirectUri = window.location.origin + this.location.prepareExternalUrl('/logout');
    } else {
      redirectUri = environment.postLogoutRedirectUri;
    }
    this.oauth.configure({
      issuer: environment.openIdConnect.issuer,
      responseType: 'code',
      scope: 'profile email roles',
      requireHttps: !environment.openIdConnect.noSSL,
      clientId: environment.openIdConnect.clientId,
      showDebugInformation: isDevMode(),
      strictDiscoveryDocumentValidation: false,
      postLogoutRedirectUri: redirectUri
    });
    return this.formatConfigService.init();
  }
}

export const applicationConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      AppRoutingModule.forRoot(),
      LoadingBarRouterModule,
      MizenAngularLibModule.forRoot(mizenAngularLibConfig()),
      TranslateModule.forRoot()
    ),
    AppConfig,
    provideAppInitializer(() => {
      const config: AppConfig = inject(AppConfig);
      return config.load();
    }),
    {
      provide: OAuthStorage,
      useFactory: prefixedOauthStorageFactory
    },
    {
      provide: AuthConfig,
      useFactory: lowerCaseAllowedUrlsFactory
    },
    {
      provide: OAuthModuleConfig,
      useFactory: lowerCaseAllowedUrlsFactory
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-CH'
    }
  ]
};

function mizenAngularLibConfig(): MizenAngularLibConfig {
  return {
    basePath: windowEnvironment.mizenBasePath
  };
}

function lowerCaseAllowedUrlsFactory(): any {
  const urls = [];
  if (windowEnvironment.apiUrl.toLowerCase()) {
    urls.push(windowEnvironment.apiUrl.toLowerCase());
  }
  if (windowEnvironment.mizenBasePath?.toLowerCase()) {
    urls.push(windowEnvironment.mizenBasePath.toLowerCase());
  }

  return {
    resourceServer: {
      // allowedUrls need to be lowerCase because angular-oauth2-oidc only adds the Authorization Header if the
      // _lowercase_ urls match
      allowedUrls: urls,
      sendAccessToken: true
    },
    ...windowEnvironment.openIdConnect
  };
}

function prefixedOauthStorageFactory(): OAuthStorage {
  return new PrefixedOAuthStorage(window.sessionStorage);
}

class PrefixedOAuthStorage implements OAuthStorage {
  prefix: string;

  constructor(private oAuthStorage: OAuthStorage) {
    this.prefix = this.createPrefix();
  }

  private createPrefix(): string {
    const app = windowEnvironment.deploymentMode === 'PUBLIC' ? 'icp' : 'capi';
    const kanton = windowEnvironment.kanton;
    return app + '_' + kanton + '_';
  }

  private getKey(key: string) {
    return this.prefix + key;
  }

  getItem(key: string): string | null {
    return this.oAuthStorage.getItem(this.getKey(key));
  }

  removeItem(key: string): void {
    this.oAuthStorage.removeItem(this.getKey(key));
  }

  setItem(key: string, data: string): void {
    this.oAuthStorage.setItem(this.getKey(key), data);
  }
}
